import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import Seo from "@/components/Seo";
import { FadeUp } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Shell({ children }) {
  return (
    <div className="flex min-h-[70vh] items-center justify-center px-6 py-24">
      <FadeUp className="w-full max-w-xl rounded-3xl border border-forest/15 bg-white/50 px-8 py-14 text-center">
        {children}
      </FadeUp>
    </div>
  );
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [paid, setPaid] = useState(false);
  const [pending, setPending] = useState(true);

  useEffect(() => {
    let alive = true;
    if (!sessionId) { setPending(false); return; }
    const poll = async (tries) => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          if (alive) { setPaid(true); setPending(false); }
          return;
        }
      } catch (e) { /* keep polling */ }
      if (tries > 0) setTimeout(() => poll(tries - 1), 2000);
      else if (alive) setPending(false);
    };
    poll(4);
    return () => { alive = false; };
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page">
      <Seo title="Payment received — Sita" description="Your deposit is confirmed. Your date is held." />
      <Shell>
        <span className="mx-auto block h-px w-14 bg-gold" aria-hidden="true" />
        {paid || pending ? (
          <>
            <h1 className="mt-6 font-display text-4xl font-light text-forest">
              {paid ? "Your date is held." : "Confirming your deposit…"}
            </h1>
            <p className="mt-4 text-ink/80">
              {paid
                ? "Thank you — your deposit is in. We'll be in touch shortly with next steps."
                : "We're confirming with Stripe. This page will update in a moment."}
            </p>
          </>
        ) : (
          <>
            <h1 className="mt-6 font-display text-4xl font-light text-forest">Almost there.</h1>
            <p className="mt-4 text-ink/80">
              We couldn't confirm your payment yet. If you completed checkout, we'll verify it and be in touch.
            </p>
          </>
        )}
        <Link
          to="/"
          data-testid="payment-success-home-link"
          className="mt-8 inline-flex rounded-full bg-terracotta px-7 py-3 text-sm font-medium text-cream transition-[background-color,color] duration-300 hover:bg-gold hover:text-deepforest"
        >
          Back to Sita
        </Link>
      </Shell>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div data-testid="payment-cancel-page">
      <Seo title="Payment paused — Sita" description="No charge was made. Your date is still open." />
      <Shell>
        <span className="mx-auto block h-px w-14 bg-gold" aria-hidden="true" />
        <h1 className="mt-6 font-display text-4xl font-light text-forest">No worries.</h1>
        <p className="mt-4 text-ink/80">Your checkout was paused and nothing was charged. Your date is still open.</p>
        <Link
          to="/inquiries"
          data-testid="payment-cancel-inquiries-link"
          className="mt-8 inline-flex rounded-full bg-terracotta px-7 py-3 text-sm font-medium text-cream transition-[background-color,color] duration-300 hover:bg-gold hover:text-deepforest"
        >
          Back to inquiries
        </Link>
      </Shell>
    </div>
  );
}
